/**
 * Helper: Web components
 * -----------------------------------------------------------------------------
 * Helper functions for building web components.
 * - All updates are run from $data object.
 * - $data object has a proxy setter on it to run watch functions and update
 *   observed attributes on update.
 * - Simpler than having some updates run from attributeChangedCallback().
 *
 */
/* eslint-disable no-undefined */
import { convertToCamelCase, convertToHandle } from '~/helpers/convert';

/**
 * Handle attribute update from outside component.
 * - Ignores updates until component setup is complete.
 * @param {Object} component - Component this reference.
 * @param {String} name - Attribute name.
 * @param {String} newValue - New attribute value.
 * @param {String} oldValue - Old attribute value.
 */
export function handleAttributeUpdate(component, name, newValue, oldValue) {
  if (oldValue === null || newValue === oldValue) {
    return;
  }

  const key = convertToCamelCase(name);
  component.$data[key] = convertType(newValue);
}

/**
 * Setup component data (attributes, computed, watchers).
 * @param {Object} component - Component this reference.
 */
export function setup(component) {
  buildAttributes(component);
  buildComputed(component);
  buildWatch(component);
}

/**
 * Mark component as active via a data attribute.
 * - Web components have no shared state machine like Vue's async components,
 *   so this is the equivalent signal for external tooling (e.g. an e2e suite)
 *   to know the custom element has attached and run its enhancement logic.
 * - Call from connectedCallback().
 * @param {Object} component - Component this reference.
 */
export function markComponentActive(component) {
  component.dataset.componentState = cnvs.states.active;
}

/**
 * Get a component's DOM element via its data-component-handle attribute.
 * - Used to reflect Pinia component state onto the DOM for components whose
 *   wrapper isn't reachable via closest(), e.g. teleported content.
 * @param {String} handle - Component handle.
 * @returns {Element|null}
 */
export function getComponentHandleElement(handle) {
  return document.querySelector(`[data-component-handle="${handle}"]`);
}

/**
 * Add observed attributes to $data.
 * @param {Object} component - Component this reference.
 */
function buildAttributes(component) {
  const observedAttributes = {};

  for (const attribute of component.constructor.observedAttributes) {
    const property = convertToCamelCase(attribute);

    if (component.$data[property] !== undefined) {
      window.console.error(
        `Can't create ${property} observed attribute as it already exists in $data`,
      );
      continue;
    }

    observedAttributes[property] = convertType(
      component.getAttribute(attribute),
    );
  }

  component.$data = {
    ...component.$data,
    ...observedAttributes,
  };
}

/**
 * Build computed data properties.
 * - Goes through computed object and gets updated values on $data update.
 * @param {Object} component - Component this reference.
 */
function buildComputed(component) {
  const computed = component.computed(component);

  for (const [key, value] of Object.entries(computed)) {
    if (component.$data[key] !== undefined) {
      window.console.error(
        `Can't create ${key} computed property as it already exists in $data`,
      );
      continue;
    }

    Object.defineProperty(component.$data, key, {
      get() {
        return value();
      },
    });
  }
}

/**
 * Build watch data.
 * - Adds proxy to $data to run watch functions on update.
 * - Proxy setter requires Reflect.set() to actually update $data.
 * @param {Object} component - Component this reference.
 */
function buildWatch(component) {
  component.$data = new Proxy(component.$data, {
    set(target, key, newValue, receiver) {
      const oldValue = target[key];

      if (newValue === oldValue) {
        return true;
      }

      /**
       * Update attribute if it's an observed attribute.
       */
      const handle = convertToHandle(key);

      if (component.constructor.observedAttributes.includes(handle)) {
        const attributeValue =
          typeof newValue === 'object' ? JSON.stringify(newValue) : newValue;

        component.setAttribute(handle, attributeValue);
      }

      /**
       * Update data and run watch function if it exists.
       */
      Reflect.set(target, key, newValue, receiver);
      const watchFunctions = component.watch(component);

      if (typeof watchFunctions[key] === 'function') {
        watchFunctions[key](newValue, oldValue);
      }

      return true;
    },
  });
}

/**
 * Convert string to relevant type.
 * @param {String} string - String to convert.
 * @returns {Array|Boolean|Number|String}
 */
function convertType(string) {
  /**
   * Handle numbers.
   */
  if (!isNaN(Number(string))) {
    return Number(string);
  }

  /**
   * Handle arrays and objects.
   * - Must be JSON-compliant, except replace " with '.
   */
  if (string.match(/(?:\[|{).*?(?:\]|})/gs)) {
    const object = string.replaceAll(`'`, '"');
    return JSON.parse(object);
  }

  /**
   * Handle boolean, strings, null, and undefined.
   * - null is converted to undefined to avoid firing watch methods on mount.
   * - JSON.stringify([undefined]) returns 'null'.
   */
  switch (string.toLowerCase()) {
    case 'false':
      return false;
    case 'true':
      return true;
    case 'null':
    case 'undefined':
      return undefined;
    default:
      return string;
  }
}
